import { Button, Loader } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';

import { LogoHeader } from 'src/Components/Shared/LogoHeader';
import { useAcceptInviteCode } from 'src/Hooks/Mutations/useAcceptInviteCode';
import useValidateInviteCode from 'src/Hooks/Queries/useValidateInviteCode';

import styles from './JoinByCodePage.module.scss';

const JoinByCodePage = () => {
  const { t } = useTranslation();
  const session = useSessionContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const hasAutoAccepted = useRef(false);

  const { data: validation, isError, isLoading: isValidating } = useValidateInviteCode(code);

  const { mutate: acceptCode, isLoading: isAccepting } = useAcceptInviteCode({
    onSuccess: data => {
      navigate(`/asset/${data.asset.id}`, { state: { showWelcome: true } });
    },
  });

  useEffect(() => {
    if (
      !session.loading &&
      session.doesSessionExist &&
      code &&
      validation &&
      !hasAutoAccepted.current
    ) {
      hasAutoAccepted.current = true;
      acceptCode(code);
    }
  }, [session, code, validation, acceptCode]);

  if (!code) {
    navigate('/');
    return null;
  }

  const isLoggedIn = !session.loading && session.doesSessionExist;

  return (
    <div className={styles.container}>
      <div className={styles.subcontainer}>
        <LogoHeader />
        {isValidating ? (
          <div className={styles.loaderContainer}>
            <Loader size="md" />
          </div>
        ) : isError ? (
          <>
            <div className={styles.title}>
              {t('joinByCode.invalidCode', "Ce code d'invitation n'est pas valide")}
            </div>
            <Button className={styles.button} onClick={() => navigate('/auth')}>
              {t('joinByCode.signInOrRegister', 'Se connecter / Créer un compte')}
            </Button>
          </>
        ) : (
          <>
            <div className={styles.title}>
              {t('joinByCode.title', 'Invitation à rejoindre')} {validation?.asset.name}
            </div>
            <div className={styles.subtitle}>
              {t('joinByCode.invitedBy', 'Vous avez été invité par ')}{' '}
              {[validation?.createdByFirstName, validation?.createdByLastName].join(' ')}
            </div>

            {isLoggedIn ? (
              <Button
                className={styles.button}
                onClick={() => acceptCode(code)}
                loading={isAccepting}
              >
                {t('joinByCode.accept', "Accepter l'invitation")}
              </Button>
            ) : (
              <>
                <div className={styles.subtitle}>
                  {t(
                    'joinByCode.pleaseConnect',
                    'Connectez-vous ou créez un compte pour accéder au bien :'
                  )}
                </div>
                <Button
                  className={styles.button}
                  onClick={() =>
                    navigate(`/auth?returnUrl=${encodeURIComponent(`/join?code=${code}`)}`)
                  }
                >
                  {t('joinByCode.signInOrRegister', 'Se connecter / Créer un compte')}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JoinByCodePage;
